import { emailRegex } from 'utilities/formsyValidators'

// Each section is a radio over these modes. Both map onto the single stored
// string: '' = no-reply (the product default), anything else = the typed
// address.
export type ReplyMode = 'no_reply' | 'address'

export type SectionState = {
  mode: ReplyMode
  address: string
}

export type AddressError = 'required' | 'invalid' | 'thanx_blocked' | null

// Reply-to must never route back to a Thanx inbox (blank already falls back to
// no-reply@thanx.com server-side). Block thanx.com and any of its subdomains at
// the field. The `@` anchor keeps look-alikes like me@nothanx.com allowed. A
// tamper-proof backend check is tracked separately (OPE-534); this is the
// primary UX guard.
const THANX_ADDRESS = /@(?:[a-z0-9-]+\.)*thanx\.com$/i

/** Derive a section's radio mode + address-field contents from the stored string. */
export function deriveSectionState(
  value: string | null | undefined
): SectionState {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return { mode: 'no_reply', address: '' }
  return { mode: 'address', address: trimmed }
}

/** Collapse a section's radio mode + address back to the single stored string. */
export function buildStoredValue(state: SectionState): string {
  return state.mode === 'address' ? state.address.trim() : ''
}

/**
 * Validate a section. Only the 'address' mode requires a value: choosing
 * "Send replies to an email address" but leaving it blank is an error, the
 * value must be a valid email, and it must not be a thanx.com address.
 * 'no_reply' never errors.
 */
export function validateSection(state: SectionState): AddressError {
  if (state.mode !== 'address') return null
  const trimmed = state.address.trim()
  if (!trimmed) return 'required'
  if (!emailRegex.test(trimmed)) return 'invalid'
  if (THANX_ADDRESS.test(trimmed)) return 'thanx_blocked'
  return null
}
